import React, { useState, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import AppLayout from '@cloudscape-design/components/app-layout';
import SideNavigation from '@cloudscape-design/components/side-navigation';
import TopNavigation from '@cloudscape-design/components/top-navigation';
import SplitPanel from '@cloudscape-design/components/split-panel';
import Table from '@cloudscape-design/components/table';
import Box from '@cloudscape-design/components/box';
import Badge from '@cloudscape-design/components/badge';
import Spinner from '@cloudscape-design/components/spinner';
import { AuthProvider, useAuth } from './components/AuthProvider';
import LoginPage from './components/LoginPage';
import JobList from './components/JobList';
import JobDetail from './components/JobDetail';
import ChatPanel from './components/ChatPanel';
import ApprovalLibrary from './components/ApprovalLibrary';

const NAV_ITEMS = [
  { type: 'link' as const, text: 'Jobs', href: '/' },
  { type: 'link' as const, text: 'Approval Library', href: '/library' },
];

/** Data passed up from JobDetail when user clicks a mapping row */
export interface FieldPreviewData {
  sourceField: string;
  targetField: string;
  records: { row: number; sourceValue: string; mappedValue: string }[];
}

/** Wrapper that clears state when navigating to job list */
function JobListPage({ onNavigate }: { onNavigate: () => void }) {
  React.useEffect(() => { onNavigate(); }, [onNavigate]);
  return <JobList />;
}

const AuthenticatedApp: React.FC = () => {
  const { userEmail, signOut } = useAuth();
  const [chatProps, setChatProps] = useState<{ jobId: string; processedBy?: string } | null>(null);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [fieldPreview, setFieldPreview] = useState<FieldPreviewData | null>(null);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);

  const reloadRef = useRef<(() => void) | null>(null);
  const reassignmentRef = useRef<((targetAccountType: string) => void) | null>(null);

  const handleMappingChange = useCallback(() => {
    reloadRef.current?.();
  }, []);

  const handleReassignmentFromChat = useCallback((targetAccountType: string) => {
    reassignmentRef.current?.(targetAccountType);
  }, []);

  const handleJobLoaded = useCallback((props: { jobId: string; processedBy?: string } | null) => {
    setChatProps(props);
    if (props?.processedBy === 'claudeagent') {
      setToolsOpen(true);
    }
  }, []);

  const handleClearAll = useCallback(() => {
    setChatProps(null);
    setToolsOpen(false);
    setSplitOpen(false);
    setFieldPreview(null);
    setPendingPrompt(null);
  }, []);

  const handleFieldSelect = useCallback((data: FieldPreviewData) => {
    setFieldPreview(data);
    setSplitOpen(true);
  }, []);

  const handleAskAgent = useCallback((prompt: string) => {
    setPendingPrompt(prompt);
    setToolsOpen(true);
  }, []);

  const handlePromptConsumed = useCallback(() => {
    setPendingPrompt(null);
  }, []);

  const showChat = chatProps?.processedBy === 'claudeagent';

  return (
    <>
      <div id="top-nav" style={{ position: 'sticky', top: 0, zIndex: 1002 }}>
        <TopNavigation
          identity={{ title: 'Migration Portal', href: '/' }}
          utilities={[
            {
              type: 'menu-dropdown',
              text: userEmail || 'User',
              iconName: 'user-profile',
              items: [{ id: 'signout', text: 'Sign out' }],
              onItemClick: ({ detail }) => {
                if (detail.id === 'signout') signOut();
              },
            },
          ]}
        />
      </div>
      <AppLayout
        headerSelector="#top-nav"
        navigation={
          <SideNavigation
            header={{ text: 'Migration Portal', href: '/' }}
            items={NAV_ITEMS}
          />
        }
        tools={
          showChat && chatProps ? (
            <ChatPanel
              jobId={chatProps.jobId}
              processedBy={chatProps.processedBy}
              onMappingChange={handleMappingChange}
              onReassignment={handleReassignmentFromChat}
              pendingPrompt={pendingPrompt}
              onPromptConsumed={handlePromptConsumed}
            />
          ) : undefined
        }
        toolsOpen={showChat && toolsOpen}
        toolsHide={!showChat}
        toolsWidth={480}
        onToolsChange={({ detail }) => setToolsOpen(detail.open)}
        ariaLabels={{ toolsToggle: 'Agent Chat' }}
        splitPanel={
          fieldPreview ? (
            <SplitPanel
              header={`All values: ${fieldPreview.sourceField} → ${fieldPreview.targetField}`}
              closeBehavior="hide"
            >
              <Table
                items={fieldPreview.records}
                columnDefinitions={[
                  {
                    id: 'row',
                    header: 'Record',
                    width: 80,
                    cell: (r) => <Badge>{`#${r.row}`}</Badge>,
                  },
                  {
                    id: 'sourceValue',
                    header: 'Source Value',
                    cell: (r) => <Box variant="code">{r.sourceValue || '—'}</Box>,
                  },
                  {
                    id: 'mappedValue',
                    header: 'Mapped Output',
                    cell: (r) => <Box variant="code">{r.mappedValue || '—'}</Box>,
                  },
                ]}
                variant="embedded"
                empty="No records"
              />
            </SplitPanel>
          ) : undefined
        }
        splitPanelOpen={splitOpen}
        onSplitPanelToggle={({ detail }) => setSplitOpen(detail.open)}
        content={
          <Routes>
            <Route path="/" element={<JobListPage onNavigate={handleClearAll} />} />
            <Route path="/library" element={<ApprovalLibrary />} />
            <Route
              path="/jobs/:jobId"
              element={
                <JobDetail
                  onJobLoaded={handleJobLoaded}
                  reloadRef={reloadRef}
                  reassignmentRef={reassignmentRef}
                  onFieldSelect={handleFieldSelect}
                  onAskAgent={handleAskAgent}
                />
              }
            />
          </Routes>
        }
      />
    </>
  );
};

const AppGate: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <Spinner size="large" />
      </Box>
    );
  }

  if (!isAuthenticated) return <LoginPage />;

  return (
    <BrowserRouter>
      <AuthenticatedApp />
    </BrowserRouter>
  );
};

const App: React.FC = () => (
  <AuthProvider>
    <AppGate />
  </AuthProvider>
);

export default App;
